/**
 * Phone number utilities for Philippine numbers
 * Handles validation and auto-formatting for mobile and landline numbers
 */

export interface PhoneFormatResult {
  formatted: string;
  isValid: boolean;
  type: 'mobile' | 'landline' | 'unknown';
}

/**
 * Country codes mapping
 */
export const COUNTRY_CODES = [
  { code: 'PHL', value: '+63', label: 'Philippines (+63)' },
  { code: 'USA', value: '+1', label: 'United States (+1)' },
  { code: 'SGP', value: '+65', label: 'Singapore (+65)' },
  { code: 'MYS', value: '+60', label: 'Malaysia (+60)' },
  { code: 'THA', value: '+66', label: 'Thailand (+66)' },
  { code: 'IDN', value: '+62', label: 'Indonesia (+62)' },
  { code: 'VNM', value: '+84', label: 'Vietnam (+84)' },
  { code: 'JPN', value: '+81', label: 'Japan (+81)' },
  { code: 'KOR', value: '+82', label: 'South Korea (+82)' },
  { code: 'CHN', value: '+86', label: 'China (+86)' }
];

/**
 * Get default country code (Philippines)
 */
export const getDefaultCountryCode = () => COUNTRY_CODES[0];

/**
 * Remove all non-digit characters from phone number
 */
export const cleanPhoneNumber = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

/**
 * Detect if number has country code prefix
 */
export const hasCountryCode = (phone: string): boolean => {
  const cleaned = cleanPhoneNumber(phone);
  return cleaned.startsWith('63') && cleaned.length > 10;
};

/**
 * Remove country code from phone number
 */
export const removeCountryCode = (phone: string): string => {
  const cleaned = cleanPhoneNumber(phone);
  if (cleaned.startsWith('63')) {
    return cleaned.substring(2);
  }
  return cleaned;
};

/**
 * Format mobile number according to Philippine standards
 * Input: 09171234567 → Output: 0917 123-4567
 * Input: +639171234567 → Output: +63 917 123-4567
 */
export const formatMobileNumber = (phone: string, includeCountryCode: boolean = false): PhoneFormatResult => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Handle numbers with country code (+639xxxxxxxxx)
  if (cleaned.startsWith('639') && cleaned.length === 13) {
    const withoutCountryCode = cleaned.substring(2); // Remove '63'
    if (withoutCountryCode.length === 11 && withoutCountryCode.startsWith('9')) {
      const formatted = `+63 ${withoutCountryCode.substring(0, 3)} ${withoutCountryCode.substring(3, 6)}-${withoutCountryCode.substring(6)}`;
      return {
        formatted,
        isValid: true,
        type: 'mobile'
      };
    }
  }
  
  // Handle international format input without + (639xxxxxxxxx)
  if (cleaned.startsWith('639') && cleaned.length === 12) {
    const withoutCountryCode = cleaned.substring(2); // Remove '63'
    if (withoutCountryCode.length === 10 && withoutCountryCode.startsWith('9')) {
      const formatted = `+63 ${withoutCountryCode.substring(0, 3)} ${withoutCountryCode.substring(3, 6)}-${withoutCountryCode.substring(6)}`;
      return {
        formatted,
        isValid: true,
        type: 'mobile'
      };
    }
  }
  
  // Handle local mobile numbers (09xxxxxxxxx)
  if (cleaned.length === 11 && cleaned.startsWith('09')) {
    if (includeCountryCode) {
      const withoutZero = cleaned.substring(1); // Remove leading 0
      const formatted = `+63 ${withoutZero.substring(0, 3)} ${withoutZero.substring(3, 6)}-${withoutZero.substring(6)}`;
      return {
        formatted,
        isValid: true,
        type: 'mobile'
      };
    } else {
      const formatted = `${cleaned.substring(0, 4)} ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
      return {
        formatted,
        isValid: true,
        type: 'mobile'
      };
    }
  }
  
  // Handle partial input for real-time formatting
  if (cleaned.length > 0) {
    // For international format starting with 639
    if (cleaned.startsWith('639')) {
      if (cleaned.length <= 3) {
        return { formatted: `+63 ${cleaned.substring(2)}`, isValid: false, type: 'mobile' };
      } else if (cleaned.length <= 6) {
        return { formatted: `+63 ${cleaned.substring(2, 5)} ${cleaned.substring(5)}`, isValid: false, type: 'mobile' };
      } else if (cleaned.length <= 9) {
        return { formatted: `+63 ${cleaned.substring(2, 5)} ${cleaned.substring(5, 8)}-${cleaned.substring(8)}`, isValid: false, type: 'mobile' };
      } else if (cleaned.length <= 12) {
        return { formatted: `+63 ${cleaned.substring(2, 5)} ${cleaned.substring(5, 8)}-${cleaned.substring(8)}`, isValid: cleaned.length === 12, type: 'mobile' };
      }
    }
    
    // For local format starting with 09
    if (cleaned.startsWith('09')) {
      if (includeCountryCode) {
        const withoutZero = cleaned.substring(1);
        if (withoutZero.length <= 3) {
          return { formatted: `+63 ${withoutZero}`, isValid: false, type: 'mobile' };
        } else if (withoutZero.length <= 6) {
          return { formatted: `+63 ${withoutZero.substring(0, 3)} ${withoutZero.substring(3)}`, isValid: false, type: 'mobile' };
        } else if (withoutZero.length <= 10) {
          return { formatted: `+63 ${withoutZero.substring(0, 3)} ${withoutZero.substring(3, 6)}-${withoutZero.substring(6)}`, isValid: withoutZero.length === 10, type: 'mobile' };
        }
      } else {
        if (cleaned.length <= 4) {
          return { formatted: cleaned, isValid: false, type: 'mobile' };
        } else if (cleaned.length <= 7) {
          return { formatted: `${cleaned.substring(0, 4)} ${cleaned.substring(4)}`, isValid: false, type: 'mobile' };
        } else if (cleaned.length <= 11) {
          return { formatted: `${cleaned.substring(0, 4)} ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`, isValid: cleaned.length === 11, type: 'mobile' };
        }
      }
    }
  }
  
  return {
    formatted: phone,
    isValid: false,
    type: 'unknown'
  };
};

/**
 * Format landline number according to Philippine standards
 * Input: 0282308511 → Output: (02) 8230-8511
 * Input: +63282308511 → Output: +63 2 8230-8511
 */
export const formatLandlineNumber = (phone: string, includeCountryCode: boolean = false): PhoneFormatResult => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Handle numbers with country code (+632xxxxxxxx)
  if (cleaned.startsWith('632') && cleaned.length === 11) {
    const withoutCountryCode = cleaned.substring(2); // Remove '63'
    if (withoutCountryCode.length === 9 && withoutCountryCode.startsWith('2')) {
      const formatted = `+63 ${withoutCountryCode.substring(0, 1)} ${withoutCountryCode.substring(1, 5)}-${withoutCountryCode.substring(5)}`;
      return {
        formatted,
        isValid: true,
        type: 'landline'
      };
    }
  }
  
  // Handle international format input without + (632xxxxxxxx)
  if (cleaned.startsWith('632') && cleaned.length === 10) {
    const withoutCountryCode = cleaned.substring(2); // Remove '63'
    if (withoutCountryCode.length === 8 && withoutCountryCode.startsWith('2')) {
      const formatted = `+63 ${withoutCountryCode.substring(0, 1)} ${withoutCountryCode.substring(1, 5)}-${withoutCountryCode.substring(5)}`;
      return {
        formatted,
        isValid: true,
        type: 'landline'
      };
    }
  }
  
  // Handle local landline numbers (02xxxxxxxx)
  if (cleaned.length === 9 && cleaned.startsWith('02')) {
    if (includeCountryCode) {
      const areaCode = cleaned.substring(1, 2); // Get '2' from '02'
      const number = cleaned.substring(2);
      const formatted = `+63 ${areaCode} ${number.substring(0, 4)}-${number.substring(4)}`;
      return {
        formatted,
        isValid: true,
        type: 'landline'
      };
    } else {
      const formatted = `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 6)}-${cleaned.substring(6)}`;
      return {
        formatted,
        isValid: true,
        type: 'landline'
      };
    }
  }
  
  // Handle partial input for real-time formatting
  if (cleaned.length > 0) {
    // For international format starting with 632
    if (cleaned.startsWith('632')) {
      if (cleaned.length <= 3) {
        return { formatted: `+63 ${cleaned.substring(2)}`, isValid: false, type: 'landline' };
      } else if (cleaned.length <= 6) {
        return { formatted: `+63 ${cleaned.substring(2, 3)} ${cleaned.substring(3)}`, isValid: false, type: 'landline' };
      } else if (cleaned.length <= 10) {
        return { formatted: `+63 ${cleaned.substring(2, 3)} ${cleaned.substring(3, 7)}-${cleaned.substring(7)}`, isValid: cleaned.length === 10, type: 'landline' };
      }
    }
    
    // For local format starting with 02
    if (cleaned.startsWith('02')) {
      if (includeCountryCode) {
        const areaCode = cleaned.substring(1, 2);
        const number = cleaned.substring(2);
        if (number.length <= 3) {
          return { formatted: `+63 ${areaCode} ${number}`, isValid: false, type: 'landline' };
        } else if (number.length <= 7) {
          return { formatted: `+63 ${areaCode} ${number.substring(0, 4)}-${number.substring(4)}`, isValid: number.length === 7, type: 'landline' };
        }
      } else {
        if (cleaned.length <= 2) {
          return { formatted: `(${cleaned})`, isValid: false, type: 'landline' };
        } else if (cleaned.length <= 5) {
          return { formatted: `(${cleaned.substring(0, 2)}) ${cleaned.substring(2)}`, isValid: false, type: 'landline' };
        } else if (cleaned.length <= 9) {
          return { formatted: `(${cleaned.substring(0, 2)}) ${cleaned.substring(2, 6)}-${cleaned.substring(6)}`, isValid: cleaned.length === 9, type: 'landline' };
        }
      }
    }
  }
  
  return {
    formatted: phone,
    isValid: false,
    type: 'unknown'
  };
};

/**
 * Auto-format phone number based on type
 */
export const autoFormatPhoneNumber = (
  phone: string, 
  type: 'mobile' | 'landline',
  includeCountryCode: boolean = false
): PhoneFormatResult => {
  if (type === 'mobile') {
    return formatMobileNumber(phone, includeCountryCode);
  } else {
    return formatLandlineNumber(phone, includeCountryCode);
  }
};

/**
 * Validate Philippine mobile number
 */
export const isValidMobileNumber = (phone: string): boolean => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Check for local format (09xxxxxxxxx)
  if (cleaned.length === 11 && cleaned.startsWith('09')) {
    return true;
  }
  
  // Check for international format (+639xxxxxxxxx)
  if (cleaned.length === 13 && cleaned.startsWith('639')) {
    return true;
  }
  
  // Check for international format without + (639xxxxxxxxx)
  if (cleaned.length === 12 && cleaned.startsWith('639')) {
    return true;
  }
  
  return false;
};

/**
 * Validate Philippine landline number
 */
export const isValidLandlineNumber = (phone: string): boolean => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Check for local format (02xxxxxxxx)
  if (cleaned.length === 9 && cleaned.startsWith('02')) {
    return true;
  }
  
  // Check for international format (+632xxxxxxxx)
  if (cleaned.length === 11 && cleaned.startsWith('632')) {
    return true;
  }
  
  // Check for international format without + (632xxxxxxxx)
  if (cleaned.length === 10 && cleaned.startsWith('632')) {
    return true;
  }
  
  return false;
};

/**
 * Validate phone number based on type
 */
export const validatePhoneNumber = (phone: string, type: 'mobile' | 'landline'): boolean => {
  if (type === 'mobile') {
    return isValidMobileNumber(phone);
  } else {
    return isValidLandlineNumber(phone);
  }
};

/**
 * Get phone number without formatting for storage
 */
export const getCleanPhoneNumber = (phone: string): string => {
  return cleanPhoneNumber(phone);
};

/**
 * Format phone number for display in real-time as user types
 */
export const formatPhoneNumberRealTime = (
  value: string,
  type: 'mobile' | 'landline',
  countryCode: string = '+63'
): string => {
  const includeCountryCode = countryCode !== '+63' || value.startsWith('+');
  const result = autoFormatPhoneNumber(value, type, includeCountryCode);
  return result.formatted;
};

/**
 * Character limits for phone numbers
 */
export const MOBILE_NUMBER_MAX_LENGTH = 13;
export const LANDLINE_NUMBER_MAX_LENGTH = 15;

/**
 * Validate mobile number with digit limit
 * Max 13 digits, formats: 09XXXXXXXXX (11 digits) or +639XXXXXXXXX (13 digits)
 */
export const validateMobileNumberWithLimit = (phone: string): boolean => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Check digit count limits:
  // Local format: 09XXXXXXXXX = 11 digits
  // International format: +639XXXXXXXXX = 13 digits
  if (cleaned.length > 13) return false;
  
  return isValidMobileNumber(phone);
};

/**
 * Check if mobile number input exceeds digit limit during typing
 * This is used for real-time input validation
 */
export const exceedsMobileDigitLimit = (phone: string): boolean => {
  const cleaned = cleanPhoneNumber(phone);
  return cleaned.length > 13;
};

/**
 * Validate landline number with character limit
 * Max 15 characters, supports Philippines area codes with extensions
 */
export const validateLandlineNumberWithLimit = (phone: string): boolean => {
  if (phone.length > LANDLINE_NUMBER_MAX_LENGTH) return false;
  return isValidLandlineNumber(phone);
};